import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Lock, Plus } from "lucide-react";
import { useWallet } from "@/components/BalanceChip";
import { DepositDialog, usePublicSettings } from "@/components/DepositDialog";
import { WithdrawDialog } from "@/components/WithdrawDialog";
import { ReferralWalletCard } from "@/components/ReferralCard";
import { api, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const FILTERS = ["", "deposit", "stake_lock", "win", "withdrawal", "refund"];

const Balances = ({ onDeposit, onWithdraw }) => {
  const { t, lang } = useI18n();
  const { data } = useWallet();
  return (
    <section className="grid gap-4 md:grid-cols-[1.4fr_1fr]">
      <div className="surface relative overflow-hidden p-6" data-testid="wallet-available-card">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/15 blur-3xl" />
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("wallet.available")}</p>
        <p className="mt-3 font-mono text-4xl font-semibold tracking-tight text-emerald-400 sm:text-5xl" data-testid="wallet-available-balance">
          {formatXAF(data?.available ?? 0, lang)}
        </p>
        {data?.to_wager > 0 && (
          <p className="mt-2 text-sm text-slate-300" data-testid="wallet-to-wager">
            {t("wallet.withdrawable")} : <b className="font-mono text-emerald-300">{formatXAF(data.withdrawable, lang)}</b> · {t("wallet.toWager", { a: formatXAF(data.to_wager, lang) })}
          </p>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button onClick={onDeposit} data-testid="wallet-deposit-btn"
            className="flex h-14 items-center justify-center gap-2 rounded-full bg-amber-500 font-display font-bold text-[#0B0D12] transition-transform active:scale-95">
            <Plus className="h-5 w-5" /> {t("wallet.deposit")}
          </button>
          <button onClick={onWithdraw} data-testid="wallet-withdraw-btn"
            className="flex h-14 items-center justify-center gap-2 rounded-full border border-white/15 font-semibold text-slate-200 transition-colors hover:border-amber-500/50">
            <ArrowUpRight className="h-5 w-5" /> {t("wallet.withdraw")}
          </button>
        </div>
      </div>
      <div className="surface flex flex-col justify-between p-6" data-testid="wallet-locked-card">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Lock className="h-3.5 w-3.5" /> {t("wallet.locked")}
          </p>
          <p className="mt-3 font-mono text-3xl font-semibold text-red-300" data-testid="wallet-locked-balance">{formatXAF(data?.locked ?? 0, lang)}</p>
          <p className="mt-1 text-xs text-slate-500">{t("wallet.lockedHint")}</p>
        </div>
        <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
          <span className="text-sm text-slate-400">{t("wallet.total")}</span>
          <span className="font-mono font-semibold" data-testid="wallet-total-balance">{formatXAF(data?.total ?? 0, lang)}</span>
        </div>
      </div>
    </section>
  );
};

const DevBanner = () => {
  const { t } = useI18n();
  const { data } = usePublicSettings();
  if (!data || data.campay_ready) return null;
  return <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="campay-not-ready-banner">{t("wallet.notReady")}</div>;
};

const EntryRow = ({ e }) => {
  const { t, lang } = useI18n();
  const delta = e.available_delta;
  const positive = delta > 0;
  return (
    <li className="flex items-center gap-4 py-4" data-testid={`ledger-entry-${e.id}`}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${positive ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
        {positive ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{t(`types.${e.type}`)}</p>
        <p className="truncate text-xs text-slate-500">{e.description || "—"} · {new Date(e.created_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</p>
      </div>
      <div className="text-right">
        <p className={`font-mono text-sm font-semibold ${positive ? "text-emerald-400" : "text-red-400"}`}>
          {positive ? "+" : ""}{formatXAF(delta, lang)}
        </p>
        <p className="font-mono text-[11px] text-slate-600">{formatXAF(e.available_after, lang)}</p>
      </div>
    </li>
  );
};

const Ledger = () => {
  const { t } = useI18n();
  const [type, setType] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["ledger", type],
    queryFn: async () => (await api.get("/wallet/ledger", { params: { type: type || undefined, limit: 50 } })).data,
  });
  return (
    <section className="surface p-5 sm:p-6" data-testid="wallet-ledger">
      <h2 className="font-display text-lg font-bold">{t("wallet.history")}</h2>
      <div className="-mx-1 mt-4 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setType(f)} data-testid={`ledger-filter-${f || "all"}`}
            className={`h-10 shrink-0 rounded-full px-4 text-xs font-semibold transition-colors ${type === f ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-400"}`}>
            {f ? t(`types.${f}`) : t("wallet.all")}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">{t("common.loading")}</p>
      ) : data?.items?.length ? (
        <ul className="divide-y divide-white/5">{data.items.map((e) => <EntryRow key={e.id} e={e} />)}</ul>
      ) : (
        <p className="py-10 text-center text-sm text-slate-500" data-testid="ledger-empty">{t("wallet.empty")}</p>
      )}
    </section>
  );
};

const STATUS_CLS = { SUCCESSFUL: "text-emerald-400", FAILED: "text-red-400", EXPIRED: "text-red-400", PENDING: "text-amber-300", REVIEW: "text-amber-300", INITIATING: "text-slate-400", AWAITING_APPROVAL: "text-amber-300", FAILING: "text-red-400" };

const Payments = ({ onOpen }) => {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });
  return (
    <section className="surface p-5 sm:p-6" data-testid="wallet-payments">
      <h2 className="font-display text-lg font-bold">{t("wallet.payments")}</h2>
      {data?.length ? (
        <ul className="mt-2 divide-y divide-white/5">
          {data.map((p) => (
            <li key={p.id}>
              <button onClick={() => (p.kind === "withdrawal" ? null : onOpen(p))} className="flex min-h-[56px] w-full items-center justify-between gap-3 py-3 text-left" data-testid={`payment-row-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t(p.kind === "withdrawal" ? "wallet.withdraw" : "wallet.deposit")} · {p.operator} · <span className="font-mono">{p.phone}</span></p>
                  <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold">{formatXAF(p.amount, lang)}</p>
                  <p className={`text-xs font-semibold ${STATUS_CLS[p.status]}`} data-testid={`payment-status-${p.id}`}>{t(`status.${p.status}`)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-8 text-center text-sm text-slate-500">{t("wallet.emptyPayments")}</p>
      )}
    </section>
  );
};

export default function WalletPage() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(null);
  const [wOpen, setWOpen] = useState(false);
  const openWith = (p) => {
    setInitial(p);
    setOpen(true);
  };
  return (
    <div className="space-y-5 rise" data-testid="wallet-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("wallet.title")}</h1>
      <DevBanner />
      <Balances onDeposit={() => openWith(null)} onWithdraw={() => setWOpen(true)} />
      <ReferralWalletCard />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <Ledger />
        <Payments onOpen={openWith} />
      </div>
      <DepositDialog open={open} onOpenChange={setOpen} initialPayment={initial} />
      <WithdrawDialog open={wOpen} onOpenChange={setWOpen} />
    </div>
  );
}
